import { type FC, useEffect, useState } from 'react'
import classNames from 'classnames'
import * as Sentry from '@sentry/react'
import { type SaveBarNotice } from './saveBarNotice'
import styles from './SaveBarNoticeStrip.module.scss'

const COPIED_RESET_MS = 2000

interface SaveBarNoticeStripProps {
  notice: SaveBarNotice
  onDismiss: () => void
  onContactSupport: () => void
}

export const SaveBarNoticeStrip: FC<SaveBarNoticeStripProps> = ({ notice, onDismiss, onContactSupport }) => {
  const isError = notice.variant === 'error'
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) {
      return undefined
    }
    const timer = window.setTimeout(() => setCopied(false), COPIED_RESET_MS)
    return () => window.clearTimeout(timer)
  }, [copied])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(notice.message)
      setCopied(true)
    } catch (error) {
      Sentry.captureException(error)
    }
  }

  return (
    <div className='d-flex align-items-center justify-content-center position-relative py-3 px-5 zocks-gap-1'>
      {isError ? (
        <i className='bi bi-exclamation-circle-fill fs-4 zocks-text-danger-200' />
      ) : (
        <span className={classNames('d-flex align-items-center justify-content-center rounded-circle p-1', styles.successIconCircle)}>
          <i className='bi bi-check-circle-fill fs-6 lh-1 zocks-text-success' />
        </span>
      )}
      <span className={classNames('fw-bold', isError ? 'zocks-text-danger-200' : 'zocks-text-success-200')}>
        {notice.message}
        {notice.withContactSupport ? (
          <>
            {' Try again or '}
            <button
              type='button'
              className={classNames('btn p-0 border-0 fw-bold align-baseline text-decoration-underline', styles.supportLink)}
              onClick={onContactSupport}
            >
              contact support.
            </button>
          </>
        ) : null}
      </span>
      <div className='d-flex align-items-center zocks-gap-0.75 position-absolute top-50 end-0 translate-middle-y me-4'>
        {isError ? (
          <button
            type='button'
            aria-label={copied ? 'Copied' : 'Copy error'}
            title={copied ? 'Copied' : 'Copy'}
            className='d-flex border-0 bg-transparent p-0 text-reset lh-1'
            onClick={() => void handleCopy()}
          >
            <i className={classNames('bi fs-6', copied ? 'bi-check' : 'bi-copy')} />
          </button>
        ) : null}
        <button type='button' aria-label='Dismiss' className='d-flex border-0 bg-transparent p-0 text-reset lh-1' onClick={onDismiss}>
          <i className='bi bi-x fs-4' />
        </button>
      </div>
    </div>
  )
}
